import { faChevronLeft, faChevronRight } from '@fortawesome/pro-duotone-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type ClassName, twx } from '@/utils/tailwind';

export const panelCollapseHandleSides = ['left', 'right'] as const;
export type PanelCollapseHandleSide = (typeof panelCollapseHandleSides)[number];

export interface PanelCollapseHandleProps {
  /** Which edge of the parent panel this handle hangs off of. */
  side: PanelCollapseHandleSide;
  /** Whether the panel is currently collapsed. Drives the chevron
   * direction: the chevron always points TOWARD where the panel will
   * open into when clicked. */
  isCollapsed: boolean;
  onClick: () => void;
  /** Accessible label — required since the button has no text. */
  ariaLabel: string;
  className?: ClassName;
}

/**
 * Slim tab affixed to the edge of a sibling panel — used to
 * collapse/expand the list panel (desktop) and the trip-drawer thumb
 * (mobile). Both inline call sites used to drift apart visually (one
 * had `bg-white h-12`, the other `bg-content-background h-10`); this
 * primitive unifies them on a single 40px white tab.
 *
 * The handle is absolutely positioned relative to its `relative` parent
 * and vertically centered. Caller decides z-index via `className` if the
 * default `z-content-overlay` isn't enough.
 */
export const PanelCollapseHandle = ({
  side,
  isCollapsed,
  onClick,
  ariaLabel,
  className,
}: PanelCollapseHandleProps) => {
  // The chevron points toward where pressing it will open the panel —
  // on a left-edge handle, collapsed → expand to the right (chevron-right);
  // on a right-edge handle, collapsed → expand to the left (chevron-left).
  const icon =
    side === 'left'
      ? isCollapsed
        ? faChevronRight
        : faChevronLeft
      : isCollapsed
        ? faChevronLeft
        : faChevronRight;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      title={ariaLabel}
      className={twx(
        'absolute top-1/2 -translate-y-1/2 z-content-overlay',
        'flex items-center justify-center w-5 h-10',
        'bg-white border border-content-divider shadow-sm',
        'text-content-subtle hover:text-content-text hover:bg-content-focal transition-colors',
        side === 'left' ? 'left-0 rounded-r-md' : 'right-0 rounded-l-md',
        className,
      )}
    >
      <FontAwesomeIcon icon={icon} style={{ width: 10, height: 10 }} />
    </button>
  );
};
